import React, { useEffect, useState } from 'react';
import BasicInfo from './steps/BasicInfo';
import Consent from './steps/Consent';
import Coverage from './steps/Coverage';
import Household from './steps/Household';
import Providers from './steps/Providers';
import Prescriptions from './steps/Prescriptions';
import Review from './steps/Review';

const STEPS = [
  { label: 'Consent', component: Consent },
  { label: 'Basic Info', component: BasicInfo },
  { label: 'Coverage', component: Coverage },
  { label: 'Household', component: Household },
  { label: 'Providers', component: Providers },
  { label: 'Prescriptions', component: Prescriptions },
  { label: 'Review', component: Review },
];

const initialData = {
  firstName: '', middleName: '', lastName: '',
  email: '', phone: '',
  address: '', address2: '', city: '', county: '', state: '', zip: '',
  mailingIsSameAsPhysical: true,
  mailingAddress: '', mailingAddress2: '', mailingCity: '', mailingState: '', mailingZip: '',
  insuranceInterests: [],
  hasCurrentInsurance: null,
  requestedStartDate: '',
  coverageFor: '',
  coverageForOther: '',
  householdSize: '',
  householdIncomeRange: '',
  householdIncome: '',
  householdMembers: [],
  preferredProviders: [''],
  doctors: [],
  prescriptions: [],
  comments: '',
  primaryWritingAgent: 'Jacquie Walter',
  agentNpn: '',
  agentPhone: '(503) 324-4511',
  agentEmail: 'support@translatinginsurance.com',
  agencyName: 'Translating Insurance',
  agencyNpn: '',
  agencyOwner: 'Jacquie Walter',
  agencyPhone: '(503) 324-4511',
  agencyEmail: 'support@translatinginsurance.com',
  consentSignature: '',
  consentAgreed: false,
  consentTimestamp: null,
};

function getParams() {
  const params = new URLSearchParams(window.location.search);
  return {
    contactId: params.get('cid') || params.get('contactId') || '',
    dealId: params.get('oid') || params.get('dealId') || '',
    inviteToken: params.get('t') || '',
    consent: params.get('consent') || '',
    intake: params.get('intake') || '',
  };
}

export default function IndFamWizard() {
  const { contactId, dealId, inviteToken, consent, intake } = getParams();
  const isConsentOnly = consent === 'only';
  const isIntakeOnly = intake === 'only';
  const storageKey = inviteToken ? `indfam_intake_${inviteToken}` : contactId ? `indfam_intake_${contactId}` : 'indfam_intake_draft';
  const stateStorageKey = `${storageKey}_state`;

  // Determine at load time (not reactive) whether consent was already submitted in a prior session
  const consentAlreadySubmitted = (() => {
    try {
      const state = JSON.parse(localStorage.getItem(stateStorageKey) || '{}');
      if (state.consentSubmitted) return true;
      const form = JSON.parse(localStorage.getItem(storageKey) || '{}');
      return !!form.consentDocumentId;
    } catch { return false; }
  })();

  const activeSteps = isConsentOnly ? [STEPS[0]] : (isIntakeOnly || consentAlreadySubmitted) ? STEPS.slice(1) : STEPS;

  const [step, setStep] = useState(0);
  const [formData, setFormData] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(storageKey) || '{}');
      return { ...initialData, ...saved };
    } catch {
      return initialData;
    }
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [consentSubmitting, setConsentSubmitting] = useState(false);
  const [consentSubmitted, setConsentSubmitted] = useState(consentAlreadySubmitted);
  const [prefillLoading, setPrefillLoading] = useState(!!inviteToken);
  const [error, setError] = useState('');

  useEffect(() => {
    localStorage.setItem(storageKey, JSON.stringify(formData));
  }, [formData, storageKey]);

  useEffect(() => {
    try {
      localStorage.setItem(stateStorageKey, JSON.stringify({ consentSubmitted }));
    } catch {}
  }, [stateStorageKey, consentSubmitted]);


  useEffect(() => {
    if (!inviteToken) return;
    let cancelled = false;

    async function loadPrefill() {
      setPrefillLoading(true);
      try {
        const res = await fetch(`/api/prefill?t=${encodeURIComponent(inviteToken)}`);
        if (!res.ok) throw new Error(`Invite prefill failed (${res.status})`);
        const payload = await res.json();
        const client = payload.clientData || payload || {};
        const nextContactId = payload.contactId || client.contactId || client.recordId || contactId || '';
        const nextDealId = payload.dealId || payload.opportunityId || client.dealId || client.opportunityId || dealId || '';
        if (!cancelled) {
          localStorage.removeItem(storageKey);
          setFormData(prev => ({
            ...prev,
            ...client,
            firstName: client.firstName || payload.firstName || prev.firstName || '',
            middleName: client.middleName || payload.middleName || prev.middleName || '',
            lastName: client.lastName || payload.lastName || prev.lastName || '',
            email: client.email || payload.email || prev.email || '',
            phone: client.phone || client.cellPhone || client.homePhone || payload.phone || prev.phone || '',
            zip: client.zip || client.postalCode || payload.zip || prev.zip || '',
            primaryWritingAgent: client.primaryWritingAgent || client.agentName || payload.agentName || prev.primaryWritingAgent,
            agentNpn: client.agentNpn || payload.agentNpn || prev.agentNpn,
            agentPhone: client.agentPhone || payload.agentPhone || prev.agentPhone,
            agentEmail: client.agentEmail || payload.agentEmail || prev.agentEmail,
            agencyName: client.agencyName || payload.agencyName || prev.agencyName,
            agencyNpn: client.agencyNpn || payload.agencyNpn || prev.agencyNpn,
            agencyOwner: client.agencyOwner || payload.agencyOwner || prev.agencyOwner,
            agencyPhone: client.agencyPhone || payload.agencyPhone || prev.agencyPhone,
            agencyEmail: client.agencyEmail || payload.agencyEmail || prev.agencyEmail,
            contactId: nextContactId || prev.contactId,
            recordId: nextContactId || prev.recordId,
            dealId: nextDealId || prev.dealId,
            opportunityId: nextDealId || prev.opportunityId,
            inviteToken,
          }));
        }
      } catch (err) {
        console.warn('Invite prefill failed', err);
      } finally {
        if (!cancelled) setPrefillLoading(false);
      }
    }

    loadPrefill();
    return () => { cancelled = true; };
  }, [contactId, dealId, inviteToken, storageKey]);

  function onChange(field, value) {
    setFormData(prev => ({ ...prev, [field]: value }));
  }

  function buildPayload() {
    const crmContactId = formData.contactId || formData.recordId || contactId;
    const crmDealId = formData.opportunityId || formData.dealId || dealId;
    const selfMember = (formData.householdMembers || []).find(m => m.relationship === 'Self');
    return {
      ...formData,
      contactId: crmContactId,
      recordId: formData.recordId || crmContactId,
      cid: formData.cid || crmContactId,
      dealId: formData.dealId || crmDealId,
      opportunityId: crmDealId,
      oid: formData.oid || crmDealId,
      dob: selfMember?.dob || formData.dob || '',
      inviteToken,
      formType: isConsentOnly ? 'indfam-consent' : isIntakeOnly ? 'indfam-intake-only' : 'indfam-intake',
      submittedAt: new Date().toISOString(),
      userAgent: navigator.userAgent || '',
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || '',
    };
  }

  async function submitConsentOnlyPayload(payload) {
    const res = await fetch('/api/submit-consent', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      const detail = await res.text();
      throw new Error(detail || `Consent submit failed (${res.status})`);
    }
    return res.json().catch(() => ({}));
  }

  function validateConsent() {
    if (!formData.consentAgreed) return 'Please check the electronic signature agreement before continuing.';
    if (!String(formData.consentSignature || '').trim()) return 'Please type your full legal name as the signature before continuing.';
    return '';
  }

  function validateBasicInfo() {
    if (!String(formData.firstName || '').trim()) return 'First name is required.';
    if (!String(formData.lastName || '').trim()) return 'Last name is required.';
    if (!String(formData.email || '').trim()) return 'Email is required.';
    if (!String(formData.phone || '').trim()) return 'Phone number is required.';
    if (!String(formData.address || '').trim()) return 'Address is required.';
    if (!String(formData.city || '').trim()) return 'City is required.';
    if (!String(formData.state || '').trim()) return 'State is required.';
    if (!String(formData.zip || '').trim()) return 'ZIP code is required.';
    if (!String(formData.county || '').trim()) return 'County is required.';
    return '';
  }

  function handleNext() {
    if (activeSteps[step].label === 'Basic Info') {
      const err = validateBasicInfo();
      if (err) { setError(err); return; }
    }
    setError('');
    setStep(i => Math.min(activeSteps.length - 1, i + 1));
  }

  async function continueFromConsent() {
    const validationError = validateConsent();
    if (validationError) {
      setError(validationError);
      return;
    }
    setError('');
    setStep(i => Math.min(activeSteps.length - 1, i + 1));
    if (formData.consentDocumentId || consentSubmitting) return;
    setConsentSubmitting(true);
    try {
      const result = await submitConsentOnlyPayload({
        ...buildPayload(),
        formType: 'indfam-consent',
      });
      setConsentSubmitted(true);
      setFormData(prev => ({
        ...prev,
        contactId: prev.contactId || result.contactId || result.clientData?.contactId || formData.contactId || contactId,
        recordId: prev.recordId || result.contactId || result.clientData?.recordId || result.clientData?.contactId || formData.recordId || formData.contactId || contactId,
        cid: prev.cid || result.contactId || result.clientData?.contactId || formData.cid || formData.contactId || contactId,
        dealId: prev.dealId || result.dealId || result.clientData?.dealId || formData.dealId || dealId,
        opportunityId: prev.opportunityId || result.dealId || result.clientData?.opportunityId || result.clientData?.dealId || formData.opportunityId || formData.dealId || dealId,
        oid: prev.oid || result.dealId || result.clientData?.dealId || formData.oid || formData.dealId || dealId,
        consentDocumentId: result.documentId || prev.consentDocumentId || '',
        consentPdfUrl: result.blobUrl || prev.consentPdfUrl || '',
      }));
    } catch (err) {
      console.warn('Consent submit failed', err);
      setError(err.message || 'Consent submit failed');
    } finally {
      setConsentSubmitting(false);
    }
  }

  async function submit() {
    setSubmitting(true);
    setError('');
    const payload = {
      ...buildPayload(),
      consentAlreadySubmitted: !!formData.consentDocumentId,
    };

    try {
      if (isConsentOnly) {
        await submitConsentOnlyPayload(payload);
      } else {
        const res = await fetch('/api/submit', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
        if (!res.ok) {
          const detail = await res.text();
          throw new Error(detail || `Submit failed (${res.status})`);
        }
      }
      localStorage.removeItem(storageKey);
      setSubmitted(true);
    } catch (err) {
      setError(err.message || 'Submit failed');
    } finally {
      setSubmitting(false);
    }
  }

  const StepComponent = activeSteps[step].component;


  if (prefillLoading) {
    return (
      <main style={s.container}>
        <section style={s.card}>
          <h1 style={s.title}>Loading Form</h1>
          <p style={s.copy}>Loading the secure invite information for this form.</p>
        </section>
      </main>
    );
  }

  if (submitted) {
    return (
      <main style={s.container}>
        <section style={s.card}>
          <h1 style={s.title}>Thank You</h1>
          <p style={s.copy}>{isConsentOnly ? 'Your consent form has been submitted.' : 'Your Individual/Family intake has been submitted.'}</p>
        </section>
      </main>
    );
  }

  return (
    <main style={s.container}>
      <div style={s.header}>
        <img src="/logo.png" alt="Translating Insurance" style={s.logoImg} />
      </div>

      <div style={s.titleBar}>{isConsentOnly ? 'INDIVIDUAL/FAMILY CONSENT FORM' : 'INDIVIDUAL/FAMILY INTAKE FORM'}</div>

      <div style={s.progressRow}>
        {activeSteps.map((st, i) => (
          <React.Fragment key={st.label}>
            <div style={s.progressItem}>
              <button
                type="button"
                onClick={() => setStep(i)}
                style={{
                  ...s.dot,
                  background: i < step ? '#1a3364' : i === step ? '#2a5298' : '#b8cfe8',
                  boxShadow: i === step ? '0 0 0 3px #dbeef8, 0 0 0 5px #2a5298' : 'none',
                  border: 'none',
                }}
              >
                {i < step ? '✓' : i + 1}
              </button>
              <span style={{
                ...s.dotLabel,
                color: i === step ? '#1a3364' : i < step ? '#2a5298' : '#8faac5',
                fontWeight: i === step ? 700 : 400,
              }}>
                {st.label}
              </span>
            </div>
            {i < activeSteps.length - 1 && (
              <div style={{ ...s.connector, background: i < step ? '#1a3364' : '#b8cfe8' }} />
            )}
          </React.Fragment>
        ))}
      </div>

      <section style={s.card}>
        <h3 style={s.stepTitle}>{activeSteps[step].label}</h3>
        <StepComponent data={formData} onChange={onChange} />

        {error && <div style={s.error}>{error}</div>}

        <footer style={s.footer}>
          <button type="button" style={s.btnBack} onClick={() => setStep(i => Math.max(0, i - 1))} disabled={step === 0}>
            Back
          </button>
          {step < activeSteps.length - 1 ? (
            <button type="button" style={s.btnNext} onClick={activeSteps[step].label === 'Consent' ? continueFromConsent : handleNext} disabled={consentSubmitting}>
              {activeSteps[step].label === 'Consent' && consentSubmitting ? 'Submitting Consent...' : 'Next'}
            </button>
          ) : (
            <button type="button" style={s.btnNext} onClick={submit} disabled={submitting}>
              {submitting ? 'Submitting...' : isConsentOnly ? 'Submit Consent' : 'Submit Form'}
            </button>
          )}
        </footer>
      </section>
    </main>
  );
}

const s = {
  container: {
    width: '100%',
    maxWidth: 700,
    margin: '0 auto',
    paddingTop: 32,
    paddingBottom: 28,
  },
  header: {
    textAlign: 'center',
    marginBottom: 12,
  },
  logoImg: {
    maxWidth: 340,
    width: '100%',
    height: 'auto',
  },
  titleBar: {
    background: '#1a3364',
    borderRadius: 8,
    padding: '13px 24px',
    textAlign: 'center',
    color: '#fff',
    fontWeight: 700,
    fontSize: 15,
    letterSpacing: '0.08em',
    marginBottom: 24,
  },
  title: { margin: '4px 0 0', color: '#1a3364', fontSize: 28, letterSpacing: 0 },
  copy: { color: '#4a6288', fontSize: 15, lineHeight: 1.6 },
  progressRow: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: 24,
    padding: '0 4px',
  },
  progressItem: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 6,
    flex: '0 0 auto',
  },
  dot: {
    width: 34,
    height: 34,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#fff',
    fontSize: 13,
    fontWeight: 700,
    transition: 'background 0.2s',
    cursor: 'pointer',
  },
  dotLabel: {
    fontSize: 11,
    textAlign: 'center',
    whiteSpace: 'nowrap',
  },
  connector: {
    flex: 1,
    height: 3,
    borderRadius: 2,
    margin: '0 4px',
    marginBottom: 22,
    transition: 'background 0.2s',
  },
  card: {
    background: '#fff',
    borderRadius: 12,
    padding: '28px 32px',
    boxShadow: '0 2px 16px rgba(26,51,100,0.10)',
    marginBottom: 16,
  },
  stepTitle: {
    color: '#1a3364',
    fontSize: 20,
    fontWeight: 700,
    marginTop: 0,
    marginBottom: 24,
    paddingBottom: 14,
    borderBottom: '2px solid #dbeef8',
  },
  footer: { display: 'flex', justifyContent: 'space-between', gap: 12, paddingTop: 18 },
  btnBack: { padding: '12px 28px', background: 'transparent', border: '2px solid #1a3364', borderRadius: 8, color: '#1a3364', fontSize: 15, fontWeight: 600, cursor: 'pointer' },
  btnNext: { padding: '12px 32px', background: '#1a3364', border: 'none', borderRadius: 8, color: '#fff', fontSize: 15, fontWeight: 600, cursor: 'pointer' },
  error: { margin: '14px 0 0', padding: 12, borderRadius: 8, background: '#fff1f1', color: '#9b1c1c', fontWeight: 700 },
};
